/**
 * The two handles a linear or radial gradient is built from.
 *
 * Both modes are described by the same pair, which is why one editor serves
 * both: for a linear ramp the handles are its two ends, and for a radial one
 * the first handle is the centre while the second sets the radius. Intermediate
 * colours ride the segment between them at positions the author can slide.
 *
 * Coordinates are normalised to the square canvas and share the app's
 * orientation — y grows downward — so nothing has to be flipped on the way to
 * Canvas2D.
 */

export interface AxisPoint {
  x: number;
  y: number;
}

/** Handles may sit outside the frame: a ramp that starts off-canvas is how you
 *  get a gradient that never fully reaches its first colour on screen. */
export const AXIS_POINT_MIN = -0.35;
export const AXIS_POINT_MAX = 1.35;

/**
 * The angle the slider shows, measured clockwise from straight up.
 *
 * This is the convention the panel already used, kept so an existing angle
 * still means the same picture after the handles were introduced.
 */
export function axisFromAngle(angle: number): { start: AxisPoint; end: AxisPoint } {
  const radians = ((angle - 90) * Math.PI) / 180;
  const dirX = Math.cos(radians);
  const dirY = Math.sin(radians);
  // Half the projection of the square onto the gradient direction, so the ramp
  // spans corner to corner rather than stopping short of them.
  const reach = (Math.abs(dirX) + Math.abs(dirY)) / 2;

  return {
    start: { x: 0.5 - dirX * reach, y: 0.5 - dirY * reach },
    end: { x: 0.5 + dirX * reach, y: 0.5 + dirY * reach }
  };
}

export function angleFromAxis(start: AxisPoint, end: AxisPoint): number {
  const degrees = (Math.atan2(end.y - start.y, end.x - start.x) * 180) / Math.PI + 90;
  return Math.round(((degrees % 360) + 360) % 360);
}

/** Rotates the handles about their own midpoint, so moving the slider re-aims
 *  the ramp without also resizing or recentring it. */
export function rotateAxis(start: AxisPoint, end: AxisPoint, angle: number): { start: AxisPoint; end: AxisPoint } {
  const midX = (start.x + end.x) / 2;
  const midY = (start.y + end.y) / 2;
  const halfLength = Math.hypot(end.x - start.x, end.y - start.y) / 2;
  const radians = ((angle - 90) * Math.PI) / 180;
  const dx = Math.cos(radians) * halfLength;
  const dy = Math.sin(radians) * halfLength;

  return {
    start: { x: midX - dx, y: midY - dy },
    end: { x: midX + dx, y: midY + dy }
  };
}

/** Evenly spaced, which is what the fixed stops used to be. */
export function defaultStopPositions(count: number): number[] {
  if (count <= 1) {
    return [0];
  }
  return Array.from({ length: count }, (_, index) => index / (count - 1));
}

/**
 * Where a pointer lands along the axis, as a fraction from start to end.
 *
 * The drag is projected onto the segment rather than following the cursor
 * freely: an intermediate stop only has one degree of freedom, and letting the
 * marker drift off the line would suggest otherwise.
 */
export function projectOntoAxis(point: AxisPoint, start: AxisPoint, end: AxisPoint): number {
  const axisX = end.x - start.x;
  const axisY = end.y - start.y;
  const lengthSquared = axisX * axisX + axisY * axisY;
  if (lengthSquared <= 1e-9) {
    return 0;
  }
  const projected = ((point.x - start.x) * axisX + (point.y - start.y) * axisY) / lengthSquared;
  return clamp(projected, 0, 1);
}

export function pointOnAxis(start: AxisPoint, end: AxisPoint, position: number): AxisPoint {
  return {
    x: start.x + (end.x - start.x) * position,
    y: start.y + (end.y - start.y) * position
  };
}

export function clampAxisPoint(point: AxisPoint): AxisPoint {
  return {
    x: clamp(point.x, AXIS_POINT_MIN, AXIS_POINT_MAX),
    y: clamp(point.y, AXIS_POINT_MIN, AXIS_POINT_MAX)
  };
}

/** Two stops on the exact same offset give a hard edge instead of a blend. */
export const STOP_GAP = 0.001;

/**
 * Clamps the stops into a valid ramp without rearranging them.
 *
 * Deliberately not a sort. This runs on every pointer move during a drag, and
 * an ordering pass that reassigns values by index is not idempotent: repeat it
 * often enough and the positions migrate between colour slots, so dragging one
 * stop drags its neighbours along with it. Ordering is instead enforced where
 * the drag happens, by holding the moving stop between the two it sits between.
 * What is left here is defence against malformed input.
 */
export function normalizeStopPositions(positions: number[], count: number): number[] {
  const result: number[] = [];

  for (let index = 0; index < count; index += 1) {
    if (index === 0) {
      result.push(0);
      continue;
    }
    if (index === count - 1) {
      result.push(1);
      continue;
    }
    const fallback = index / (count - 1);
    const raw = Number.isFinite(positions[index]) ? positions[index] : fallback;
    result.push(clamp(raw, STOP_GAP, 1 - STOP_GAP));
  }

  for (let index = 1; index < count - 1; index += 1) {
    result[index] = Math.max(result[index], result[index - 1] + STOP_GAP);
  }
  for (let index = count - 2; index >= 1; index -= 1) {
    result[index] = Math.min(result[index], result[index + 1] - STOP_GAP);
  }

  return result;
}

/** Where a dragged stop is allowed to land: up against its neighbours, never
 *  past them, and never dragging them along. */
export function clampStopBetweenNeighbours(positions: number[], index: number, wanted: number): number {
  const lower = index === 0 ? 0 : positions[index - 1];
  const upper = index >= positions.length - 1 ? 1 : positions[index + 1];
  return clamp(wanted, lower + STOP_GAP, upper - STOP_GAP);
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
